import React, { useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Dialog } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { entities } from "byzantine";
import { CardOffersUnenrollModalProps } from "./types";
import styles from "./UnenrollModal.module.scss";

const CardLinkedOffersUnenrollModal = ({
  isOpen,
  onClose,
  onSuccess,
  testId,
}: CardOffersUnenrollModalProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { deleteEnrollment, loading: isUnenrolling } =
    entities.cardLinkedOffers.useDeleteCardLinkedOffersEnrollment();

  const handleUnenroll = useCallback(() => {
    deleteEnrollment({
      onSuccess: () => {
        sendNotification({
          type: "success",
          text: l10n.getString(
            "card-linked-offers-unenroll-success",
            null,
            "Card offers deactivated."
          ),
        });

        onClose();
        onSuccess();
      },
      onError: () => {
        sendNotification({
          type: "negative",
          text: l10n.getString(
            "card-linked-offers-unenroll-error",
            null,
            "Unable to deactivate card offers. Please try again later."
          ),
        });
      },
    });
  }, [deleteEnrollment, onClose, onSuccess, sendNotification, l10n]);

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString(
        "card-offers-unenroll-modal-title",
        null,
        "Deactivate card offers"
      )}
      testId={testId}
    >
      <div className={styles.content}>
        {/* Message Section */}
        <div className={styles.messageSection}>
          <div className={styles.message}>
            {l10n.getString(
              "card-offers-unenroll-modal-message",
              null,
              "Are you sure you want to deactivate card offers? This will remove offers from all your cards and cancel any pending rewards. You can reactivate anytime."
            )}
          </div>
        </div>

        {/* Actions Section */}
        <div className={styles.actions}>
          <Button
            kind="plain"
            label={l10n.getString(
              "card-offers-unenroll-modal-close-button",
              null,
              "Close"
            )}
            onClick={onClose}
            testId="close-button"
          />
          <Button
            kind="primary"
            isLoading={isUnenrolling}
            label={l10n.getString(
              "card-offers-unenroll-modal-confirm-button",
              null,
              "Yes, deactivate"
            )}
            onClick={handleUnenroll}
            testId="confirm-unenroll-button"
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CardLinkedOffersUnenrollModal;
